// Stub-tree generator: the fallback for a celestialData body with no
// authored tree (the planets have theirs, and moons.js, smallbodies.js and
// deepsky.js cover the rest), built from celestialData so the panel still
// shows its description, numbers and references.

import { celestialData } from '../celestialData.js';

export function buildStubTree(bodyId) {
  const data = celestialData[bodyId];
  if (!data) return null;

  const children = [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Size, distance, orbit',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'Type',              value: data.type },
            { label: 'Diameter',          value: data.realDiameter },
            { label: 'Distance from Sun', value: data.realDistance },
            { label: 'Orbital period',    value: data.orbitalPeriod },
            { label: 'Moons',             value: String(data.moons) },
            { label: 'Axial tilt',        value: `${data.tilt}°` }
          ].filter(e => e.value && e.value !== 'undefined')
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',    url: `https://nssdc.gsfc.nasa.gov/planetary/factsheet/${bodyId === 'sun' ? 'sun' : bodyId}fact.html` },
            { label: `Wikipedia: ${data.name}`, url: `https://en.wikipedia.org/wiki/${encodeURIComponent(data.name)}` },
            { label: 'JPL Horizons',       url: 'https://ssd.jpl.nasa.gov/horizons/' }
          ]
        }
      ]
    }
  ];

  // Add moons subtree if there are major moons defined
  if (data.majorMoons && data.majorMoons.length) {
    children.splice(1, 0, {
      id: 'moons',
      label: data.majorMoons.length === 1 ? 'Moon' : 'Major moons',
      summary: `${data.moons} total, ${data.majorMoons.length} major`,
      children: data.majorMoons.map(moon => ({
        id: `moon-${moon.name.toLowerCase()}`,
        label: moon.name,
        summary: moon.description,
        payload: [
          { type: 'description', text: moon.description },
          {
            type: 'stats',
            entries: [
              { label: 'Type',     value: moon.type },
              { label: 'Diameter', value: moon.realDiameter },
              { label: 'Distance from planet', value: moon.realDistance },
              { label: 'Orbital period', value: moon.orbitalPeriod }
            ].filter(e => e.value)
          }
        ]
      }))
    });
  }

  // Single moon (Earth-only in current data, but supported)
  if (data.moon) {
    children.splice(1, 0, {
      id: 'moon',
      label: data.moon.name,
      summary: data.moon.description,
      payload: [
        { type: 'description', text: data.moon.description },
        {
          type: 'stats',
          entries: [
            { label: 'Diameter',         value: data.moon.realDiameter },
            { label: 'Distance from planet', value: data.moon.realDistance },
            { label: 'Orbital period',   value: data.moon.orbitalPeriod }
          ]
        }
      ]
    });
  }

  return {
    id: bodyId,
    label: data.name,
    type: data.type,
    summary: data.description,
    payload: [
      { type: 'description', text: data.description },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter',          value: data.realDiameter },
          { label: 'Distance from Sun', value: data.realDistance },
          { label: 'Orbital period',    value: data.orbitalPeriod },
          { label: 'Moons',             value: String(data.moons) }
        ]
      }
    ],
    children
  };
}
